import { FilterKey } from "../Types/filter.types";
import { FilterConfigItem } from "../Types/TableFilter.types";

// ─── Field-to-Option Key Mapping ───────────────────────────────────────────────
export const FIELD_TO_OPTION_KEY: Record<string, FilterKey> = {
  clearer_name: "clearer",
  book_name_resolved: "book",
  customers: "customers",
  commodity_name: "commodity",
  clearing_account_name: "clearingAccount",
  physical_code: "physicalCode",
  broker_name: "broker",
  buy_sell: "buySell",
  single_diff: "singleDiff",
  trader_type: "trader_type",
  contract_type_name: "contract_type_name",
  derivative: "derivative",
  strategy: "strategy",
  trade_status: "trade_status",
  marker_tas_type: "tas_mm",
};

// ─── Field Label Map ───────────────────────────────────────────────────────────
export const FIELD_LABEL_MAP: Record<string, string> = {
  derivative: "Derivative",
  trader_type: "Trade Type",
  contract_type_name: "Contract Type",
  clearer_name: "Clearer",
  book_name_resolved: "Book",
  customers: "Customers",
  strategy: "Strategy",
  buy_sell: "Buy/Sell",
  commodity_name: "Commodity",
  clearing_account_name: "Clearing Account",
  physical_code: "Physical Code",
  broker_name: "Broker",
  single_diff: "Single/Diff",
  exec_id: "Exec ID",
  tradeDateStart: "Trade Date Start",
  tradeDateEnd: "Trade Date End",
  pricingFromStart: "Pricing From Start",
  pricingFromEnd: "Pricing From End",
  pricingToStart: "Pricing To Start",
  pricingToEnd: "Pricing To End",
  priceStart: "Price Start",
  priceEnd: "Price End",
  quantityStart: "Quantity Start",
  quantityEnd: "Quantity End",
  trade_status: "Trade Status",
  marker_tas_type: "Tas Type",
};

// ─── Range Validation Map ──────────────────────────────────────────────────────
export const RANGE_VALIDATION_MAP: Record<string, { start: string; end: string }> = {
  priceEnd: { start: "priceStart", end: "priceEnd" },
  quantityEnd: { start: "quantityStart", end: "quantityEnd" },
};

export const TRADER = "Trader"; 
export const TRADER_MAPPING_NAME = "Exchange Trader Name"; 
export const DELETING = "Deleting..."; 
export const UPDATE = "Update"; 
export const ADD = "Add"; 

export const EXCHANGE_COMPANY_NAME = "Exchange Company Name";
export const COMPANY_NAME = "Company Name";

export const BROKER_MAPPING = "Broker Mapping";
export const BROKER_MAPPING_NAME = "Broker Mapping Name";

// ─── Date Preset Names ─────────────────────────────────────────────────────────
export const DATE_PRESET_TODAY = "Today";
export const DATE_PRESET_YESTERDAY = "Yesterday";
export const DATE_PRESET_THIS_WEEK = "This Week";
export const DATE_PRESET_THIS_MONTH = "This Month";
export const DATE_PRESET_PREVIOUS_MONTH = "Previous Month";
export const DATE_PRESET_CUSTOM = "Custom";

// ─── Filter State Field Keys ───────────────────────────────────────────────────
export const FILTER_FIELD_CLEARER_NAME = "clearer_name";
export const FILTER_FIELD_BROKER_NAME = "broker_name";
export const FILTER_FIELD_COMMODITY_NAME = "commodity_name";
export const FILTER_FIELD_CLEARING_ACCOUNT_NAME = "clearing_account_name";
export const FILTER_FIELD_BOOK_NAME_RESOLVED = "book_name_resolved";
export const FILTER_FIELD_CUSTOMERS = "customers";
export const FILTER_FIELD_STRATEGY = "strategy";
export const FILTER_FIELD_PHYSICAL_CODE = "physical_code";
export const FILTER_FIELD_TRADER_TYPE = "trader_type";
export const FILTER_FIELD_EXEC_ID = "exec_id";
export const FILTER_FIELD_SINGLE_DIFF = "single_diff";
export const FILTER_FIELD_BUY_SELL = "buy_sell";
export const FILTER_FIELD_CONTRACT_TYPE_NAME = "contract_type_name";
export const FILTER_FIELD_DERIVATIVE = "derivative";
export const FILTER_FIELD_TRADER_MAPPING = "trader_mapping";

export const FILTER_FIELD_PRICING_FROM_START = "pricingFromStart";
export const FILTER_FIELD_PRICING_FROM_END = "pricingFromEnd";
export const FILTER_FIELD_PRICING_FROM_PRESET = "pricingFromPreset";
export const FILTER_FIELD_PRICING_TO_START = "pricingToStart";
export const FILTER_FIELD_PRICING_TO_END = "pricingToEnd";
export const FILTER_FIELD_PRICING_TO_PRESET = "pricingToPreset";
export const FILTER_FIELD_TRADE_DATE_START = "tradeDateStart";
export const FILTER_FIELD_TRADE_DATE_END = "tradeDateEnd";
export const FILTER_FIELD_TRADE_DATE_PRESET = "tradeDatePreset";
export const FILTER_FIELD_PRICE_START = "priceStart";
export const FILTER_FIELD_PRICE_END = "priceEnd";
export const FILTER_FIELD_QUANTITY_START = "quantityStart";
export const FILTER_FIELD_QUANTITY_END = "quantityEnd";
export const FILTER_FIELD_TRADE_STATUS = "trade_status";
export const FILTER_FIELD_TAS_MM = "marker_tas_type";

// ─── Filter Config ─────────────────────────────────────────────────────────────
// NOTE: options are injected at runtime from filterOptions prop
export const STATIC_FILTER_CONFIG: Omit<FilterConfigItem, "options">[] = [
  { field: FILTER_FIELD_DERIVATIVE, label: "Derivative" },
  { field: FILTER_FIELD_TRADER_TYPE, label: "Trade Type" },
  { field: FILTER_FIELD_CONTRACT_TYPE_NAME, label: "Contract Type" },
  { field: FILTER_FIELD_CLEARER_NAME, label: "Clearer" },
  { field: FILTER_FIELD_BOOK_NAME_RESOLVED, label: "Book" },
  { field: FILTER_FIELD_CUSTOMERS, label: "Book" },
  { field: FILTER_FIELD_STRATEGY, label: "Strategy" },
  { field: FILTER_FIELD_BUY_SELL, label: "Buy/Sell" },
  { field: FILTER_FIELD_COMMODITY_NAME, label: "Commodity" },
  { field: FILTER_FIELD_CLEARING_ACCOUNT_NAME, label: "Clearing Account" },
  { field: FILTER_FIELD_PHYSICAL_CODE, label: "Physical Code" },
  { field: FILTER_FIELD_BROKER_NAME, label: "Broker" },
  { field: FILTER_FIELD_SINGLE_DIFF, label: "Single/Diff" },
];

// ─── Partial-All Deselection Threshold ────────────────────────────────────────
export const PARTIAL_ALL_LIMIT = 5;

// ─── Max Visible Chip Values ───────────────────────────────────────────────────
export const MAX_VISIBLE_CHIP_VALUES = 4;

// ─── UI Labels ─────────────────────────────────────────────────────────────────
export const LABEL_FILTERS = "Filters";
export const LABEL_FILTERS_ACTIVE = "Filters (Active)";
export const LABEL_CLEAR_ALL = "Clear All";
export const LABEL_APPLY = "Apply";
export const LABEL_INVALID_RANGE = " (Invalid range)";
export const LABEL_ALL = "All";
export const LABEL_ALL_EXCEPT_PREFIX = "All except ";
export const LABEL_N_SELECTED_SUFFIX = " Selected";
export const LABEL_DATE_RANGE_SEPARATOR = " to ";
export const LABEL_TRADE_DATE = "Trade Date";
export const LABEL_TRADE_STATUS = "Trade Status";

// Local Storage Constants of Contract

export const MRT_COLUMN_FILTERS_CONTRACT = 'mrt_columnFilters_table_contract'
export const MRT_COLUMN_VISIBILITY_CONTRACT = 'mrt_columnVisibility_table_contract'
export const MRT_DENSITY_CONTRACT = 'mrt_density_table_contract'
export const MRT_GLOBAL_FILTERS_CONTRACT = 'mrt_globalFilter_table_contract'
export const MRT_SHOW_GLOBAL_FILTERS_CONTRACT = 'mrt_showGlobalFilter_table_contract'
export const MRT_SHOW_COLUMN_FILTERS_CONTRACT = 'mrt_showColumnFilters_table_contract'
export const MRT_SORTING_CONTRACT = 'mrt_sorting_table_contract'

// Local Storage Constants of Brokerage

export const MRT_COLUMN_FILTERS_BROKERAGE = 'mrt_columnFilters_table_brokerage'
export const MRT_COLUMN_VISIBILITY_BROKERAGE = 'mrt_columnVisibility_table_brokerage'
export const MRT_DENSITY_BROKERAGE = 'mrt_density_table_brokerage'
export const MRT_GLOBAL_FILTERS_BROKERAGE = 'mrt_globalFilter_table_brokerage'
export const MRT_SHOW_GLOBAL_FILTERS_BROKERAGE = 'mrt_showGlobalFilter_table_brokerage'
export const MRT_SHOW_COLUMN_FILTERS_BROKERAGE = 'mrt_showColumnFilters_table_brokerage'
export const MRT_SORTING_BROKERAGE = 'mrt_sorting_table_brokerage'

// Local Storage Constants of Counterparty Companies

export const MRT_COLUMN_FILTERS_COUNTERPARTY_COMPANIES = 'mrt_columnFilters_table_counterparty_companies'
export const MRT_COLUMN_VISIBILITY_COUNTERPARTY_COMPANIES = 'mrt_columnVisibility_table_counterparty_companies'
export const MRT_DENSITY_COUNTERPARTY_COMPANIES = 'mrt_density_table_counterparty_companies'
export const MRT_GLOBAL_FILTERS_COUNTERPARTY_COMPANIES = 'mrt_globalFilter_table_counterparty_companies'
export const MRT_SHOW_GLOBAL_FILTERS_COUNTERPARTY_COMPANIES = 'mrt_showGlobalFilter_table_counterparty_companies'
export const MRT_SHOW_COLUMN_FILTERS_COUNTERPARTY_COMPANIES = 'mrt_showColumnFilters_table_counterparty_companies'
export const MRT_SORTING_COUNTERPARTY_COMPANIES = 'mrt_sorting_table_counterparty_companies'

// Local Storage Constants of API Settings

export const MRT_COLUMN_FILTERS_API_SETTINGS = 'mrt_columnFilters_table_api_settings'
export const MRT_COLUMN_VISIBILITY_API_SETTINGS = 'mrt_columnVisibility_table_api_settings'
export const MRT_DENSITY_API_SETTINGS = 'mrt_density_table_api_settings'
export const MRT_GLOBAL_FILTERS_API_SETTINGS = 'mrt_globalFilter_table_api_settings'
export const MRT_SHOW_GLOBAL_FILTERS_API_SETTINGS = 'mrt_showGlobalFilter_table_api_settings'
export const MRT_SHOW_COLUMN_FILTERS_API_SETTINGS = 'mrt_showColumnFilters_table_api_settings'
export const MRT_SORTING_API_SETTINGS = 'mrt_sorting_table_api_settings'

// Local Storage Constants of API Settings

export const MRT_COLUMN_FILTERS_CLEARER_RATES = 'mrt_columnFilters_table_clearer_rates'
export const MRT_COLUMN_VISIBILITY_CLEARER_RATES = 'mrt_columnVisibility_table_clearer_rates'
export const MRT_DENSITY_CLEARER_RATES = 'mrt_density_table_clearer_rates'
export const MRT_GLOBAL_FILTERS_CLEARER_RATES = 'mrt_globalFilter_table_clearer_rates'
export const MRT_SHOW_GLOBAL_FILTERS_CLEARER_RATES = 'mrt_showGlobalFilter_table_clearer_rates'
export const MRT_SHOW_COLUMN_FILTERS_CLEARER_RATES = 'mrt_showColumnFilters_table_clearer_rates'
export const MRT_SORTING_CLEARER_RATES = 'mrt_sorting_table_clearer_rates'

// Local Storage Constants of API Settings

export const MRT_COLUMN_FILTERS_CLEARING_ACCOUNT = 'mrt_columnFilters_table_clearing_account'
export const MRT_COLUMN_VISIBILITY_CLEARING_ACCOUNT = 'mrt_columnVisibility_table_clearing_account'
export const MRT_DENSITY_CLEARING_ACCOUNT = 'mrt_density_table_clearing_account'
export const MRT_GLOBAL_FILTERS_CLEARING_ACCOUNT = 'mrt_globalFilter_table_clearing_account'
export const MRT_SHOW_GLOBAL_FILTERS_CLEARING_ACCOUNT = 'mrt_showGlobalFilter_table_clearing_account'
export const MRT_SHOW_COLUMN_FILTERS_CLEARING_ACCOUNT = 'mrt_showColumnFilters_table_clearing_account'
export const MRT_SORTING_CLEARING_ACCOUNT = 'mrt_sorting_table_clearing_account'

// Local Storage Constants of Counterparty Companies

export const MRT_COLUMN_FILTERS_COUNTERPARTY_TABLE = 'mrt_columnFilters_table_counterparty_table'
export const MRT_COLUMN_VISIBILITY_COUNTERPARTY_TABLE = 'mrt_columnVisibility_table_counterparty_table'
export const MRT_DENSITY_COUNTERPARTY_TABLE = 'mrt_density_table_counterparty_table'
export const MRT_GLOBAL_FILTERS_COUNTERPARTY_TABLE = 'mrt_globalFilter_table_counterparty_table'
export const MRT_SHOW_GLOBAL_FILTERS_COUNTERPARTY_TABLE = 'mrt_showGlobalFilter_table_counterparty_table'
export const MRT_SHOW_COLUMN_FILTERS_COUNTERPARTY_TABLE = 'mrt_showColumnFilters_table_counterparty_table'
export const MRT_SORTING_COUNTERPARTY_TABLE = 'mrt_sorting_table_counterparty_table'

// Local Storage Constants of Counterparty Companies

export const MRT_COLUMN_FILTERS_TRADE_CAPTURE = 'mrt_columnFilters_table_trade_capture'
export const MRT_COLUMN_VISIBILITY_TRADE_CAPTURE = 'mrt_columnVisibility_table_trade_capture'
export const MRT_DENSITY_TRADE_CAPTURE = 'mrt_density_table_trade_capture'
export const MRT_GLOBAL_FILTERS_TRADE_CAPTURE = 'mrt_globalFilter_table_trade_capture'
export const MRT_SHOW_GLOBAL_FILTERS_TRADE_CAPTURE = 'mrt_showGlobalFilter_table_trade_capture'
export const MRT_SHOW_COLUMN_FILTERS_TRADE_CAPTURE = 'mrt_showColumnFilters_table_trade_capture'
export const MRT_SORTING_TRADE_CAPTURE = 'mrt_sorting_table_trade_capture'

// Local Storage Constants of System Admin

export const MRT_COLUMN_FILTERS_SYSTEM_ADMIN = 'mrt_columnFilters_table_system_admin'
export const MRT_COLUMN_VISIBILITY_SYSTEM_ADMIN = 'mrt_columnVisibility_table_system_admin'
export const MRT_DENSITY_SYSTEM_ADMIN = 'mrt_density_table_system_admin'
export const MRT_GLOBAL_FILTERS_SYSTEM_ADMIN = 'mrt_globalFilter_table_system_admin'
export const MRT_SHOW_GLOBAL_FILTERS_SYSTEM_ADMIN = 'mrt_showGlobalFilter_table_system_admin'
export const MRT_SHOW_COLUMN_FILTERS_SYSTEM_ADMIN = 'mrt_showColumnFilters_table_system_admin'
export const MRT_SORTING_SYSTEM_ADMIN = 'mrt_sorting_table_system_admin'

